/**
 * _normalize.js — Normalización de respuestas y errores para services de perfil
 *
 * Centraliza la lógica de desenvuelto de envelopes y extracción de errores
 * para que los composables trabajen con contratos consistentes sin lidiar
 * con las inconsistencias del backend.
 *
 * CONTRATO DE ÉXITO:
 *   - `toObject(payload)`    → {} siempre (nunca null/undefined)
 *   - `toArray(payload)`     → [] siempre (nunca null/undefined)
 *   - `toPaginated(payload)` → { items: [], meta: { current_page, last_page, total, per_page } }
 *
 * CONTRATO DE ERROR:
 *   - `toError(err)`         → { message: string, status: number|null, raw: Error }
 */

// ─── Helpers internos ─────────────────────────────────────────────────────────

/**
 * Extrae el contenido real de un envelope `{ data: ... }` del backend.
 * Si no existe ese envelope, devuelve el payload tal cual.
 *
 * @param {*} payload  Respuesta directa de response.data
 * @returns {*}
 */
function unwrap(payload) {
    if (
        payload !== null &&
        payload !== undefined &&
        typeof payload === 'object' &&
        'data' in payload
    ) {
        return payload.data;
    }
    return payload;
}

// ─── Normalización de éxito ───────────────────────────────────────────────────

/**
 * Garantiza un objeto plano. Útil para respuestas escalares como stats,
 * datos de sesión, resultado de bloqueo, etc.
 *
 * El backend puede devolver:
 *   - { data: { total, consumidas, restantes } }  → { total, consumidas, restantes }
 *   - { total, consumidas, restantes }             → idem
 *   - null / undefined                             → {}
 *
 * @param {*} payload  response.data de axios
 * @returns {Object}
 */
export function toObject(payload) {
    const inner = unwrap(payload);
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return inner;
    }
    return {};
}

/**
 * Garantiza un array. Útil para respuestas de listados simples (eventos, zonas, etc.)
 *
 * El backend puede devolver:
 *   - [...]            → [...]
 *   - { data: [...] }  → [...]
 *   - null / undefined → []
 *
 * @param {*} payload  response.data de axios
 * @returns {Array}
 */
export function toArray(payload) {
    if (Array.isArray(payload)) return payload;
    const inner = unwrap(payload);
    if (Array.isArray(inner)) return inner;
    return [];
}

/**
 * Garantiza una estructura paginada consistente.
 *
 * El backend puede devolver varias formas:
 *   - Laravel paginado estándar: { data: [], current_page, last_page, total, per_page }
 *   - Meta separado:             { data: [], meta: { current_page, last_page, per_page, total } }
 *   - Envelope anidado:          { data: { data: [], meta: {...} } }
 *   - Array plano sin meta:      [...]
 *
 * @param {*} payload  response.data de axios
 * @returns {{ items: Array, meta: { current_page: number, last_page: number, total: number, per_page: number } }}
 */
export function toPaginated(payload) {
    let rawItems;
    let rawMeta;

    // Detectar shape paginado de Laravel estándar ANTES de hacer unwrap:
    // { current_page, data: [...], last_page, total, per_page }
    // Si hacemos unwrap primero perdemos la meta porque 'data' es el array.
    if (
        payload !== null &&
        payload !== undefined &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        'current_page' in payload &&
        Array.isArray(payload.data)
    ) {
        rawItems = payload.data;
        rawMeta = payload;
    } else if (
        // Detectar shape { data: [], meta: { current_page, last_page, per_page, total } }
        // ANTES de hacer unwrap para conservar el meta separado.
        payload !== null &&
        payload !== undefined &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        Array.isArray(payload.data) &&
        payload.meta !== null &&
        payload.meta !== undefined &&
        typeof payload.meta === 'object'
    ) {
        rawItems = payload.data;
        rawMeta = payload.meta;
    } else {
        // Primer nivel de unwrap: { data: X } → X (envelope simple)
        const outer = unwrap(payload);

        if (Array.isArray(outer)) {
            // Respuesta plana sin paginación
            rawItems = outer;
            rawMeta = {};
        } else if (outer && typeof outer === 'object') {
            // Puede tener data[] directamente o un meta separado
            if ('current_page' in outer && Array.isArray(outer.data)) {
                // Shape paginado Laravel dentro del envelope
                rawItems = outer.data;
                rawMeta = outer;
            } else if (outer.items !== undefined) {
                rawItems = outer.items;
                rawMeta = outer.meta ?? outer;
            } else {
                rawItems = [];
                rawMeta = {};
            }
        } else {
            rawItems = [];
            rawMeta = {};
        }
    }

    const items = Array.isArray(rawItems) ? rawItems : [];
    const meta = {
        current_page: rawMeta.current_page ?? rawMeta.meta?.current_page ?? 1,
        last_page:    rawMeta.last_page    ?? rawMeta.meta?.last_page    ?? 1,
        total:        rawMeta.total        ?? rawMeta.meta?.total        ?? items.length,
        per_page:     rawMeta.per_page     ?? rawMeta.meta?.per_page     ?? items.length,
    };

    return { items, meta };
}

// ─── Normalización de error ───────────────────────────────────────────────────

/**
 * Extrae un error normalizado de cualquier excepción lanzada por axios u otras.
 *
 * El backend puede responder con diferentes shapes de error:
 *   - { message: '...' }
 *   - { error: '...' }
 *   - { errors: { field: ['...'] } }  (validación Laravel)
 *   - Sin body (network error / timeout)
 *
 * El composable siempre recibe:
 *   - `message`  → string legible para mostrar al usuario o para logs
 *   - `status`   → código HTTP (null si no hay respuesta HTTP)
 *   - `raw`      → el error original para depuración o re-lanzado si se necesita
 *
 * @param {Error} err  Error capturado en el composable
 * @returns {{ message: string, status: number|null, raw: Error }}
 */
export function toError(err) {
    const status = err?.response?.status ?? null;
    const responseData = err?.response?.data;

    let message = 'Ha ocurrido un error inesperado.';

    if (responseData) {
        if (typeof responseData === 'string') {
            message = responseData;
        } else if (typeof responseData.message === 'string' && responseData.message) {
            message = responseData.message;
        } else if (typeof responseData.error === 'string' && responseData.error) {
            message = responseData.error;
        } else if (responseData.errors && typeof responseData.errors === 'object') {
            // Validación Laravel: tomar el primer mensaje de validación disponible
            const firstField = Object.values(responseData.errors)[0];
            if (Array.isArray(firstField) && firstField.length > 0) {
                message = firstField[0];
            }
        }
    } else if (err?.message) {
        // Network error, timeout, etc.
        message = err.message;
    }

    return { message, status, raw: err };
}
